import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { CASES, fmtUsd, CASE_THUMBS, optimize, TIER_META } from "../../data/casesData";
import { useAuth } from "../../context/AuthContext";
import { useCredit } from "../../context/CreditContext";
import VerticalReel from "./VerticalReel";
import ItemImage from "./ItemImage";
import {
  Swords, Plus, X, Trophy, Users, Play, RotateCcw, ArrowLeft, Bot, Crown, Loader2, Package, LogIn, Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const caseById = Object.fromEntries(CASES.map((c) => [c.id, c]));
const SEAT_COLORS = ["#2563EB", "#DB2777", "#7C3AED", "#0891B2"];
const TEAM_COLORS = ["#2563EB", "#DB2777"];
const MODES = [
  { key: "1v1", players: 2, teamSize: 1, label: "1v1" },
  { key: "1v1v1", players: 3, teamSize: 1, label: "1v1v1" },
  { key: "1v1v1v1", players: 4, teamSize: 1, label: "1v1v1v1" },
  { key: "2v2", players: 4, teamSize: 2, label: "2v2" },
];

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return e?.message || "Something went wrong";
};

const hydrate = (caseObj, it) => caseObj.items.find((x) => x.name === it.name) || { ...it, icon: Package };

function ItemChip({ it }) {
  const color = TIER_META[it.tier]?.color || "#8A90A0";
  return (
    <div className="flex items-center gap-2 bg-[#19142A] dark:bg-white/5 border border-[#141021] dark:border-white/10 px-2 py-1 rounded-lg" data-testid="battle-item-chip">
      <span className="relative h-7 w-7 flex items-center justify-center shrink-0">
        <span className="absolute inset-0 rounded-full blur-md opacity-60" style={{ background: `radial-gradient(circle, ${color} 0%, transparent 70%)` }} />
        <ItemImage item={it} imgClass="h-6 w-6" iconClass="h-4 w-4" color={color} />
      </span>
      <span className="text-[10px] leading-tight truncate flex-1 text-white/80 dark:text-white/70">{it.name}</span>
      <span className="text-[10px] font-bold" style={{ color }}>{fmtUsd(it.value)}</span>
    </div>
  );
}

export default function BattleSection() {
  const { user } = useAuth();
  const { refreshCredits } = useCredit();
  const [phase, setPhase] = useState("lobby"); // lobby | setup | waiting | live | done
  const [lobby, setLobby] = useState([]);
  const [err, setErr] = useState("");

  // setup
  const [mode, setMode] = useState(MODES[0]);
  const [picks, setPicks] = useState([]); // case objects

  // active battle
  const [battle, setBattle] = useState(null);
  const [round, setRound] = useState(0);
  const [spinKey, setSpinKey] = useState(0);
  const [revealCount, setRevealCount] = useState(0);
  const [countdown, setCountdown] = useState(null); // 3,2,1 before round 0
  const doneRef = useRef(0);

  // ---- lobby polling ----
  useEffect(() => {
    if (phase !== "lobby") return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/battles`);
        if (!stop) setLobby(data.battles || []);
      } catch (_) {}
    };
    load();
    const iv = setInterval(load, 3000);
    return () => { stop = true; clearInterval(iv); };
  }, [phase]);

  // ---- waiting polling ----
  useEffect(() => {
    if (phase !== "waiting" || !battle) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/battles/${battle.id}`);
        if (stop) return;
        setBattle(data);
        if (data.status === "running") enterLive(data);
      } catch (_) {}
    };
    const iv = setInterval(poll, 1800);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [phase, battle?.id]);

  const enterLive = (b) => {
    setBattle(b);
    setRound(0);
    setRevealCount(0);
    doneRef.current = 0;
    setSpinKey(0);
    setCountdown(3);
    setPhase("live");
  };

  // 3-2-1 countdown, then kick off the first round's spin.
  useEffect(() => {
    if (phase !== "live" || countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      setSpinKey(1);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 850);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const onReelDone = () => {
    doneRef.current += 1;
    if (doneRef.current >= (battle?.players.length || 1)) {
      doneRef.current = 0;
      setRevealCount(round + 1);
      setTimeout(() => {
        if (round + 1 < battle.case_ids.length) {
          setRound(round + 1);
          setSpinKey((k) => k + 1);
        } else {
          setPhase("done");
        }
      }, 650);
    }
  };

  // ---- actions ----
  const requireAuth = () => {
    if (user) return true;
    window.dispatchEvent(new CustomEvent("uc:open-auth"));
    return false;
  };

  // Tie-break 50/50 wheel: when a battle ends tied, spin between the tied names.
  const [tieSpin, setTieSpin] = useState(false);
  const [tieName, setTieName] = useState("");
  useEffect(() => {
    if (phase === "done" && battle?.tie_break) {
      setTieSpin(true);
      const names = battle.players.map((p) => (p.user_id === user?.id ? "You" : p.name));
      let i = 0;
      const iv = setInterval(() => { setTieName(names[i % names.length]); i += 1; }, 100);
      const to = setTimeout(() => {
        clearInterval(iv);
        const wp = battle.players.find((p) => p.seat === battle.winner_seat);
        setTieName(wp && wp.user_id === user?.id ? "You" : (wp?.name || battle.winner_name));
        setTimeout(() => setTieSpin(false), 1100);
      }, 2300);
      return () => { clearInterval(iv); clearTimeout(to); };
    }
    setTieSpin(false);
    return undefined;
  }, [phase, battle?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createBattle = async () => {
    setErr("");
    if (!requireAuth()) return;
    try {
      const { data } = await axios.post(`${API}/battles`,
        { max_players: mode.players, case_ids: picks.map((c) => c.id), team_size: mode.teamSize }, { headers: authHeader() });
      refreshCredits();
      setBattle(data);
      setPhase("waiting");
    } catch (e) { setErr(errMsg(e)); }
  };

  const joinBattle = async (b, seat = null) => {
    setErr("");
    if (!requireAuth()) return;
    try {
      const body = seat != null ? { seat } : {};
      const { data } = await axios.post(`${API}/battles/${b.id}/join`, body, { headers: authHeader() });
      refreshCredits();
      if (data.status === "running") enterLive(data);
      else { setBattle(data); setPhase("waiting"); }
    } catch (e) { setErr(errMsg(e)); }
  };

  // Open a battle from the lobby: participants resume; everyone else spectates
  // (waiting → the room where they can pick an open seat/team; running → watch).
  const viewBattle = async (b) => {
    setErr("");
    try {
      const { data } = await axios.get(`${API}/battles/${b.id}`);
      if (data.status === "waiting") { setBattle(data); setPhase("waiting"); }
      else enterLive(data);
    } catch (e) { setErr(errMsg(e)); }
  };

  const startBots = async () => {
    setErr("");
    try {
      const { data } = await axios.post(`${API}/battles/${battle.id}/start_bots`, {}, { headers: authHeader() });
      if (data.status === "running") enterLive(data);
    } catch (e) { setErr(errMsg(e)); }
  };

  const backToLobby = () => { setBattle(null); setPicks([]); setCountdown(null); setPhase("lobby"); setErr(""); };

  const recreateBattle = async () => {
    setErr("");
    try {
      const { data } = await axios.post(`${API}/battles`,
        { max_players: battle.max_players, case_ids: battle.case_ids, team_size: battle.team_size || 1 }, { headers: authHeader() });
      refreshCredits();
      setBattle(data);
      setCountdown(null);
      setRevealCount(0);
      setRound(0);
      setPhase("waiting");
    } catch (e) { setErr(errMsg(e)); }
  };

  const addCase = (c) => setPicks((p) => (p.length >= 20 ? p : [...p, c]));
  const removeAt = (i) => setPicks((p) => p.filter((_, idx) => idx !== i));

  const mySeat = useMemo(
    () => battle?.players.find((p) => p.user_id === user?.id)?.seat ?? -1,
    [battle, user]
  );

  // running totals during live (from revealed rounds), final from backend
  const liveTotals = useMemo(() => {
    if (!battle?.results) return {};
    const t = {};
    for (const pl of battle.players) {
      let s = 0;
      const arr = battle.results[String(pl.seat)] || [];
      for (let r = 0; r < revealCount; r++) s += arr[r]?.value || 0;
      t[pl.seat] = s;
    }
    return t;
  }, [battle, revealCount]);

  // ===================== LOBBY =====================
  if (phase === "lobby") {
    const waiting = lobby.filter((b) => b.status === "waiting");
    const running = lobby.filter((b) => b.status !== "waiting");
    const Row = ({ b }) => {
      const cs = b.case_ids.map((id) => caseById[id]).filter(Boolean);
      const isJoinable = b.status === "waiting" && b.players.length < b.max_players && !b.players.some((p) => p.user_id === user?.id);
      const mine = b.players.some((p) => p.user_id === user?.id);
      return (
        <div data-testid="battle-lobby-row"
          className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 bg-[#19142A] dark:bg-[#19142A] border border-[#2A2440] dark:border-[#2A2440] rounded-lg p-3 shadow-sm hover:border-[#3A2F5C] dark:hover:border-[#3A2F5C] transition-colors">
          <div className="flex items-center gap-2 sm:w-40 shrink-0">
            <span className="h-8 w-8 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: SEAT_COLORS[0] }}>
              <Crown className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white dark:text-white truncate">{b.creator_name}</div>
              <div className="text-[11px] text-[#8A90A0] flex items-center gap-1"><Users className="h-3 w-3" /> {b.players.length}/{b.max_players}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 sm:hidden">
              <div className="text-sm font-cases uppercase bg-[#19142A] dark:bg-[#2A2440] text-white dark:text-white px-2.5 py-1">{fmtUsd(b.cost)}</div>
              {isJoinable ? (
                <button data-testid="battle-join-btn-mobile" onClick={() => (b.team_size > 1 ? viewBattle(b) : joinBattle(b))} className="px-3 py-1.5 rounded-lg font-cases uppercase text-xs bg-[#7C4DFF] text-white flex items-center gap-1.5"><Swords className="h-3.5 w-3.5" /> Join</button>
              ) : (
                <button data-testid="battle-view-btn-mobile" onClick={() => viewBattle(b)} className="px-3 py-1.5 rounded-lg font-cases uppercase text-xs border border-[#2A2440] dark:border-[#2A2440] text-white dark:text-white flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> {mine ? "Resume" : "Watch"}</button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto pb-0.5 -mx-1 px-1">
            {cs.map((c, i) => (
              <div key={i} className="h-12 w-12 sm:h-10 sm:w-10 shrink-0 border border-[#141021] bg-gradient-to-b from-[#19142A] to-[#141021] flex items-center justify-center">
                <img src={optimize(CASE_THUMBS[c.id], 120)} alt={c.name} className="h-[82%] w-[82%] object-contain" />
              </div>
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <span className={`text-[10px] uppercase font-bold px-2 py-1 ${b.status === "waiting" ? "text-[#16A34A] bg-[#E7F6EC]" : "text-[#8A90A0] bg-[#19142A]"}`}>{b.status === "waiting" ? "Open" : (mine ? "Yours" : "Live")}</span>
            <div className="text-sm font-cases uppercase bg-[#19142A] dark:bg-[#2A2440] text-white dark:text-white px-2.5 py-1">{fmtUsd(b.cost)}</div>
            {isJoinable ? (
              <button data-testid="battle-join-btn" onClick={() => (b.team_size > 1 ? viewBattle(b) : joinBattle(b))} className="px-4 py-2 rounded-lg font-cases uppercase text-xs bg-[#7C4DFF] text-white hover:bg-[#6A3AE6] flex items-center gap-1.5 transition-colors"><Swords className="h-3.5 w-3.5" /> Join</button>
            ) : (
              <button data-testid="battle-view-btn" onClick={() => viewBattle(b)} className="px-4 py-2 rounded-lg font-cases uppercase text-xs border border-[#2A2440] dark:border-[#2A2440] text-white dark:text-white hover:bg-[#19142A] dark:hover:bg-[#2A2440] flex items-center gap-1.5 transition-colors"><Eye className="h-3.5 w-3.5" /> {mine ? "Resume" : "Watch"}</button>
            )}
          </div>
        </div>
      );
    };
    return (
      <div data-testid="battle-lobby">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Swords className="h-5 w-5 text-white" />
            <h2 className="font-cases text-lg uppercase tracking-tight text-white dark:text-white">Case Battles</h2>
          </div>
          <button data-testid="battle-create-btn" onClick={() => { setPicks([]); setMode(MODES[0]); setErr(""); setPhase("setup"); }}
            className="px-4 py-2 rounded-lg font-cases uppercase tracking-wide text-sm flex items-center gap-2 bg-[#7C4DFF] text-white hover:bg-[#6A3AE6] transition-colors">
            <Plus className="h-4 w-4" /> Create Battle
          </button>
        </div>
        {err && <div className="text-xs text-[#DB2777] bg-[#FDE8F1] border border-[#F5C2D9] px-3 py-2 mb-3">{err}</div>}

        {waiting.length === 0 && running.length === 0 ? (
          <div className="bg-[#19142A] dark:bg-[#19142A] border border-dashed border-[#2A2440] dark:border-[#2A2440] rounded-lg p-10 text-center text-sm text-[#8A90A0]">
            No battles yet. Hit <span className="font-semibold text-white">Create Battle</span> and invite a friend to join.
          </div>
        ) : (
          <div className="space-y-2.5">
            {waiting.map((b) => <Row key={b.id} b={b} />)}
            {running.slice(0, 8).map((b) => <Row key={b.id} b={b} />)}
          </div>
        )}
      </div>
    );
  }

  // ===================== SETUP =====================
  if (phase === "setup") {
    const totalCost = picks.reduce((s, c) => s + c.price, 0);
    return (
      <div data-testid="battle-setup">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-2">
            <Swords className="h-5 w-5 text-white" />
            <h2 className="font-cases text-lg uppercase tracking-tight text-white dark:text-white">Create a Case Battle</h2>
          </div>
          <button onClick={backToLobby} data-testid="battle-lobby-btn"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-[#6B7280] dark:text-[#9AA1B2] hover:text-white dark:hover:text-white flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Lobby
          </button>
        </div>
        {err && <div className="text-xs text-[#DB2777] bg-[#FDE8F1] border border-[#F5C2D9] px-3 py-2 mb-3">{err}</div>}

        <div className="bg-[#19142A] dark:bg-[#19142A] border border-[#2A2440] dark:border-[#2A2440] rounded-lg p-4 mb-5 shadow-sm">
          <div className="text-xs font-semibold text-[#6B7280] dark:text-[#9AA1B2] mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Mode</div>
          <div className="flex items-center gap-2 flex-wrap">
            {MODES.map((m) => (
              <button key={m.key} data-testid={`battle-mode-${m.key}`} onClick={() => setMode(m)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${mode.key === m.key ? "bg-[#7C4DFF] text-white border-[#15102A]" : "bg-[#19142A] dark:bg-[#1E1832] text-[#6B7280] dark:text-[#9AA1B2] border-[#2A2440] dark:border-[#2A2440] hover:text-white dark:hover:text-white"}`}>
                {m.label}
              </button>
            ))}
          </div>
          {mode.teamSize > 1 && (
            <div className="text-[11px] text-[#8A90A0] mt-2 flex items-center gap-1.5" data-testid="battle-team-note">
              <Users className="h-3 w-3" /> Team up 2v2 — the winning team's pulls are split evenly between you and your teammate.
            </div>
          )}
        </div>

        <div className="bg-[#19142A] dark:bg-[#19142A] border border-[#2A2440] dark:border-[#2A2440] rounded-lg p-4 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-[#6B7280] dark:text-[#9AA1B2]">Selected cases ({picks.length})</div>
            <div className="text-sm font-cases uppercase bg-[#19142A] dark:bg-[#2A2440] text-white dark:text-white px-2.5 py-1">{fmtUsd(totalCost)}</div>
          </div>
          {picks.length === 0 ? (
            <div className="text-xs text-[#9AA0AE] py-3 text-center border border-dashed border-[#2A2440]">Click cases below to add them to the battle</div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {picks.map((c, i) => (
                <div key={i} className="relative w-16">
                  <div className="aspect-square bg-gradient-to-b from-[#19142A] to-[#141021] border border-[#2A2440] flex items-center justify-center overflow-hidden">
                    <img src={optimize(CASE_THUMBS[c.id], 160)} alt={c.name} className="h-[85%] w-[85%] object-contain" />
                  </div>
                  <button onClick={() => removeAt(i)} data-testid="battle-remove-case"
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-[#DB2777] text-white flex items-center justify-center rounded-full shadow"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <button data-testid="battle-start-btn" disabled={!picks.length} onClick={createBattle}
            className="mt-4 w-full py-2.5 rounded-lg font-cases uppercase tracking-wide text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-[#7C4DFF] text-white hover:bg-[#6A3AE6]">
            <Play className="h-4 w-4" /> Create Battle · {fmtUsd(totalCost)}
          </button>
        </div>

        <div className="text-xs font-semibold text-[#6B7280] dark:text-[#9AA1B2] mb-2">Add cases</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {CASES.map((c) => (
            <button key={c.id} data-testid="battle-add-case" onClick={() => addCase(c)}
              className="group relative bg-[#19142A] dark:bg-[#19142A] border border-[#2A2440] dark:border-[#2A2440] hover:border-[#15102A] dark:hover:border-[#9945FF] rounded-lg overflow-hidden transition-colors text-left">
              <div className="h-1 w-full" style={{ background: c.accent }} />
              <div className="aspect-square flex items-center justify-center bg-gradient-to-b from-[#19142A] to-[#141021]">
                <img src={optimize(CASE_THUMBS[c.id], 200)} alt={c.name} loading="lazy" className="h-[82%] w-[82%] object-contain group-hover:scale-105 transition-transform" />
              </div>
              <div className="p-1.5">
                <div className="font-cases text-[11px] uppercase leading-tight line-clamp-1 text-white dark:text-white">{c.name}</div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[10px] font-bold text-white dark:text-white">{fmtUsd(c.price)}</span>
                  <span className="h-4 w-4 bg-[#7C4DFF] text-white flex items-center justify-center"><Plus className="h-3 w-3" /></span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ===================== WAITING =====================
  if (phase === "waiting" && battle) {
    const rounds = battle.case_ids.map((id) => caseById[id]).filter(Boolean);
    const isCreator = battle.creator_id === user?.id;
    const shareUrl = `${window.location.origin}/cases`;
    const wTeamSize = battle.team_size || 1;
    const wIsTeam = wTeamSize > 1;
    const wSeatColor = (seat) => (wIsTeam ? TEAM_COLORS[Math.floor(seat / wTeamSize)] : SEAT_COLORS[seat]);
    const amIIn = battle.players.some((p) => p.user_id === user?.id);
    const canJoinSeat = !amIIn && battle.status === "waiting" && battle.players.length < battle.max_players;
    return (
      <div data-testid="battle-waiting">
        <div className="flex items-center justify-between mb-5 gap-2">
          <div className="flex items-center gap-2">
            <Swords className="h-5 w-5 text-white" />
            <h2 className="font-cases text-lg uppercase tracking-tight text-white dark:text-white">{amIIn ? "Waiting for players…" : "Join this battle"}</h2>
          </div>
          <button onClick={backToLobby} data-testid="battle-cancel-btn"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-[#6B7280] dark:text-[#9AA1B2] hover:text-white dark:hover:text-white flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Lobby
          </button>
        </div>
        {err && <div className="text-xs text-[#DB2777] bg-[#FDE8F1] border border-[#F5C2D9] px-3 py-2 mb-3">{err}</div>}

        <div className="bg-[#19142A] dark:bg-[#19142A] border border-[#2A2440] dark:border-[#2A2440] rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
            {Array.from({ length: battle.max_players }).map((_, seat) => {
              const pl = battle.players.find((p) => p.seat === seat);
              const joinable = !pl && canJoinSeat;
              const Wrapper = joinable ? "button" : "div";
              const teamLetter = String.fromCharCode(65 + Math.floor(seat / wTeamSize));
              return (
                <Wrapper key={seat}
                  {...(joinable ? { onClick: () => joinBattle(battle, seat), "data-testid": `battle-join-seat-${seat}`, title: `Join ${wIsTeam ? `Team ${teamLetter}` : "this seat"} · ${fmtUsd(battle.cost)}` } : {})}
                  className={`flex flex-col items-center gap-2 w-24 py-4 rounded-lg border transition-colors ${pl ? "border-[#15102A]" : joinable ? "border-dashed border-[#16A34A] hover:bg-[#E7F6EC] cursor-pointer" : "border-dashed border-[#2A2440]"}`}>
                  {wIsTeam && <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: wSeatColor(seat) }}>Team {teamLetter}</span>}
                  <span className="h-12 w-12 rounded-full flex items-center justify-center text-white" style={{ background: pl ? wSeatColor(seat) : joinable ? "#16A34A" : "#C7CCD6" }}>
                    {pl ? (pl.user_id === user?.id ? <Crown className="h-5 w-5" /> : <Bot className="h-5 w-5" />) : joinable ? <Plus className="h-5 w-5" /> : <Loader2 className="h-5 w-5 animate-spin" />}
                  </span>
                  <span className="text-xs font-semibold text-white dark:text-white truncate max-w-[80px]">{pl ? pl.name : joinable ? "Join" : "Waiting…"}</span>
                </Wrapper>
              );
            })}
          </div>
          <div className="text-center">
            <div className="text-sm font-cases uppercase text-white">{battle.players.length}/{battle.max_players} joined · {fmtUsd(battle.cost)}</div>
            <div className="text-xs text-[#8A90A0] mt-1">{canJoinSeat ? (wIsTeam ? "Pick an open seat above to join a team — winnings are split with your teammate." : "Pick an open seat above to join this battle.") : "Others can join from the Battles lobby. It starts automatically once everyone's in."}</div>
          </div>
          <div className="flex gap-1.5 mt-5 justify-center flex-wrap">
            {rounds.map((c, i) => (
              <div key={i} className="h-10 w-10 border border-[#141021] bg-gradient-to-b from-[#19142A] to-[#141021] flex items-center justify-center">
                <img src={optimize(CASE_THUMBS[c.id], 120)} alt="" className="h-[80%] w-[80%] object-contain" />
              </div>
            ))}
          </div>
          {isCreator && (
            <div className="mt-6 flex flex-col items-center gap-2">
              <button onClick={startBots} data-testid="battle-fill-bots"
                className="px-4 py-2 rounded-lg font-cases uppercase text-xs border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-white hover:bg-[#19142A] dark:hover:bg-[#2A2440] flex items-center gap-1.5 transition-colors">
                <Bot className="h-3.5 w-3.5" /> Start vs bots instead
              </button>
              <span className="text-[11px] text-[#9AA0AE]">Tired of waiting? Fill the empty seats with bots.</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===================== LIVE / DONE =====================
  if ((phase === "live" || phase === "done") && battle) {
    const rounds = battle.case_ids.map((id) => caseById[id]).filter(Boolean);
    const currentCase = rounds[Math.min(round, rounds.length - 1)];
    const teamSize = battle.team_size || 1;
    const isTeam = teamSize > 1;
    const teamOf = (seat) => Math.floor(seat / teamSize);
    const colorFor = (seat) => (isTeam ? TEAM_COLORS[teamOf(seat)] : SEAT_COLORS[seat]);
    const winnerSeats = battle.winner_seats || (battle.winner_seat != null ? [battle.winner_seat] : []);
    const winnerPlayer = battle.players.find((p) => p.seat === battle.winner_seat);
    const iWon = battle.players.some((p) => p.user_id === user?.id && winnerSeats.includes(p.seat));
    const winnerTeamName = isTeam ? `Team ${String.fromCharCode(65 + (battle.winner_team || 0))}` : winnerPlayer?.name;
    const pot = battle.totals ? Object.values(battle.totals).reduce((s, v) => s + v, 0) : 0;
    const totalItems = battle.results ? Object.values(battle.results).reduce((s, arr) => s + arr.length, 0) : 0;
    return (
      <div data-testid="battle-arena">
        {tieSpin && (
          <div data-testid="battle-tie-wheel" className="fixed inset-0 z-[140] flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="font-cases uppercase text-2xl text-white mb-1 tracking-tight">It's a tie!</div>
            <div className="text-xs text-white/60 mb-6">Spinning a fair 50/50 — winner takes all</div>
            <div className="relative h-40 w-40 rounded-full border-4 border-[#F5B301] animate-spin"
              style={{ background: "conic-gradient(#2563EB 0 50%, #DB2777 50% 100%)" }}>
            </div>
            <div className="mt-6 px-6 py-3 bg-[#19142A] dark:bg-[#141021] rounded-lg border border-[#F5B301]">
              <span data-testid="tie-wheel-name" className="font-cases uppercase text-xl text-white dark:text-white">{tieName || "…"}</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Swords className="h-5 w-5 text-white" />
            <h2 className="font-cases text-lg uppercase tracking-tight text-white dark:text-white">
              {phase === "done" ? "Battle Result" : `Battle · Round ${round + 1}/${rounds.length}`}
            </h2>
          </div>
          <button onClick={backToLobby} data-testid="battle-back-btn"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-[#6B7280] dark:text-[#9AA1B2] hover:text-white dark:hover:text-white flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Lobby
          </button>
        </div>

        {/* rounds progress */}
        <div className="flex gap-1.5 mb-4 flex-wrap">
          {rounds.map((c, i) => (
            <div key={i} className={`h-9 w-9 flex items-center justify-center border overflow-hidden ${i === round && phase === "live" ? "border-[#15102A]" : "border-[#2A2440]"} ${i < revealCount ? "opacity-100" : "opacity-40"}`} style={{ background: "#fff" }}>
              <img src={optimize(CASE_THUMBS[c.id], 120)} alt="" className="h-[80%] w-[80%] object-contain" />
            </div>
          ))}
        </div>

        <div className="relative">
          {countdown !== null && countdown > 0 && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#090C14]/75 backdrop-blur-[2px]" data-testid="battle-countdown">
              <span className="text-white/60 font-cases uppercase text-xs tracking-[0.3em] mb-2">Get ready</span>
              <span key={countdown} className="font-cases text-white text-7xl sm:text-8xl leading-none animate-ping-once" style={{ textShadow: "0 0 40px rgba(255,255,255,0.45)" }}>{countdown}</span>
            </div>
          )}
          <div className="grid gap-2 sm:gap-3" style={{ gridTemplateColumns: `repeat(${battle.players.length}, minmax(0, 1fr))` }}>
          {[...battle.players].sort((a, b) => a.seat - b.seat).map((pl) => {
            const isWinner = phase === "done" && winnerSeats.includes(pl.seat);
            const seatColor = colorFor(pl.seat);
            const wins = (battle.results && battle.results[String(pl.seat)]) || [];
            const winnerItem = wins[round] ? hydrate(currentCase, wins[round]) : null;
            return (
              <div key={pl.seat} data-testid={`battle-player-${pl.seat}`}
                className={`rounded-lg border overflow-hidden flex flex-col bg-[#19142A] dark:bg-[#19142A] ${isWinner ? "border-[#F5B301]" : "border-[#141021] dark:border-[#2A2440]"}`}>
                {isTeam && (
                  <div className="text-[9px] font-bold uppercase tracking-wide text-center py-0.5 text-white" style={{ background: seatColor }} data-testid={`battle-team-tag-${pl.seat}`}>
                    Team {String.fromCharCode(65 + teamOf(pl.seat))}
                  </div>
                )}
                <div className="flex items-center justify-between px-2 sm:px-3 py-2 border-b border-[#2A2440] dark:border-white/5">
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <span className="h-6 w-6 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: seatColor }}>
                      {pl.user_id === user?.id ? <Crown className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                    </span>
                    <span className="text-[11px] sm:text-xs font-semibold text-white dark:text-white truncate">{pl.user_id === user?.id ? "You" : pl.name}</span>
                    {isWinner && <Trophy className="h-4 w-4 text-[#F5B301] shrink-0" />}
                  </div>
                  <div className="text-[11px] sm:text-xs font-bold shrink-0" style={{ color: seatColor }}>{fmtUsd(liveTotals[pl.seat] || 0)}</div>
                </div>

                {phase === "live" ? (
                  <VerticalReel caseObj={currentCase} winner={winnerItem} spinKey={spinKey} accent={seatColor} onDone={onReelDone} />
                ) : null}

                <div className="p-1.5 sm:p-2 flex flex-col gap-1.5">
                  {wins.slice(0, revealCount).map((it, ri) => <ItemChip key={ri} it={hydrate(rounds[ri], it)} />)}
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {phase === "done" && (
          <div className={`mt-5 bg-[#19142A] dark:bg-[#19142A] border-2 rounded-lg p-4 flex items-center justify-center gap-3 shadow-sm ${iWon ? "border-[#16A34A]" : "border-[#F5B301]"}`} data-testid="battle-winner-banner">
            <Trophy className={`h-6 w-6 ${iWon ? "text-[#16A34A]" : "text-[#F5B301]"}`} />
            <span className="font-cases uppercase text-lg text-white dark:text-white text-center">
              {iWon
                ? (isTeam ? `Your team wins ${fmtUsd(pot)}` : `You win ${fmtUsd(pot)}`)
                : `${winnerTeamName} wins ${fmtUsd(pot)}`}
            </span>
          </div>
        )}
        {phase === "done" && (
          <>
            <div className="mt-2 text-center text-xs text-[#8A90A0]">
              {isTeam
                ? `${totalItems} item${totalItems !== 1 ? "s" : ""} worth ${fmtUsd(pot)} split evenly across the winning team${iWon ? " — your share is in your inventory." : "."}`
                : `Winner takes all — ${totalItems} item${totalItems !== 1 ? "s" : ""} worth ${fmtUsd(pot)}${iWon ? " added to your inventory." : "."}`}
            </div>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button onClick={recreateBattle} data-testid="battle-recreate-btn"
                className="px-5 py-2.5 rounded-lg font-cases uppercase text-sm bg-[#7C4DFF] dark:bg-[#9945FF] text-white hover:bg-[#6A3AE6] dark:hover:bg-[#8236e6] flex items-center gap-2 transition-colors">
                <RotateCcw className="h-4 w-4" /> Recreate battle · {fmtUsd(battle.cost)}
              </button>
              <button onClick={backToLobby} data-testid="battle-done-lobby-btn"
                className="px-5 py-2.5 rounded-lg font-cases uppercase text-sm border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-white hover:bg-[#19142A] dark:hover:bg-[#2A2440] flex items-center gap-2 transition-colors">
                <ArrowLeft className="h-4 w-4" /> Lobby
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}
